import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {SettlementTemplate} from "../../../../shared/interface/settlement-template";
import {NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {SettlementTemplateService} from "../../../../shared/services/settlement-template.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {AuthService} from "../../../../shared/services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {FilterService} from "../../../../shared/services/filter.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../../shared/ngrx/reducers";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../../shared/interface/response/counted-and-paginated";
import {Invoice} from "../../../../shared/interface/invoice";
import {getCurrentRouteState} from "../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../shared/ngrx/route-serializer";
import {Sort} from "@angular/material/sort";
import {RiskConfiguration} from "../../../../shared/interface/risk-configuration";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {OfwcaAddDialogComponent} from "../../../ofwcas/ofwca-add-dialog/ofwca-add-dialog.component";
import {AddSettlementsTemplateComponent} from "../add-settlements-template/add-settlements-template.component";
import {AddSettlementsTemplateDialogComponent} from "../add-settlements-template-dialog/add-settlements-template-dialog.component";

@Component({
  selector: 'app-all-settlements-template-list',
  templateUrl: './all-settlements-template-list.component.html',
  styleUrls: ['./all-settlements-template-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class AllSettlementsTemplateListComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() embeddedView = false;

  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Output() initialFiltersSettlementTemplate = new EventEmitter<string>();

  initialFilters: any;
  items: SettlementTemplate[] = [];
  expandedElement: SettlementTemplate = null;
  saveExpandedElement: SettlementTemplate = null;
  totalData = 0;
  displayedColumns: string[] = [
    'billingMonth', 'createdAt', 'company', 'typeOfSettlement', 'status'
  ];

  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'billingMonth';
  sortOrder = 'desc';
  modify = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private settlementTemplateService: SettlementTemplateService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.settlementTemplateService.settlementTemplateListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<SettlementTemplate>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;

          if (this.initialFilters?.uuid) {
            this.expandElement(this.items[0]);
          }

          if (this.saveExpandedElement) {
            this.items.forEach((settlementTemplate: SettlementTemplate) => {
              if (settlementTemplate.uuid === this.saveExpandedElement.uuid) {
                this.expandedElement = settlementTemplate;
              }
            });
            this.saveExpandedElement = null;
          }
        })
      ).subscribe();
    this.parseFilters();

  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      uuid: 'string',
      billingMonth: 'date',
      company: 'int',
      typeOfSettlement: 'int',
      active: 'int',
      used: 'int',
    }, filters);
    this.initialFiltersSettlementTemplate.emit(this.initialFilters);
  }
  ngOnInit(): void {
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((routerState: RouterStateUrl) => {
        this.parseFilters(routerState.params);
        this.paginator.pageIndex = routerState.params.page;
        this.paginator.pageSize = routerState.params.pageSize;
        this.sortBy = routerState.params?.sortBy ? routerState.params.sortBy : 'billingMonth';
        this.sortOrder = routerState.params?.sortOrder ? routerState.params.sortOrder : 'desc';
        this.loadListData();
      })
    ;
  }

  loadListData(): void {
    this.settlementTemplateService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      });
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }
  buildFilters(): any {
    const filters = {...this.initialFilters};

    return filters;
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    if (!this.embeddedView) {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        sortBy: $event.active,
        sortOrder: $event.direction,
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })]);
    } else {
      this.loadListData();
    }
  }

  expandElement(settlementTemplate: SettlementTemplate): void {
    this.expandedElement = this.expandedElement === settlementTemplate ? null : settlementTemplate;
    this.modify = false;
  }

  modificationClosed(): void {
    this.saveExpandedElement = this.expandedElement;
    this.loadListData();
  }

  addSettlementTemplate(): void
  {
    const dialogRef = this.dialog.open(AddSettlementsTemplateDialogComponent, {
      width: '90%',
      height: '90%',
      maxHeight: '90%',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
    });
  }
}
